import { memo, Fragment, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { withRouter } from 'react-router'
import styled from 'styled-components'
import { Box, Notice, Text } from '@machobear/design-system'
import isTruthy from '@/utils/is-truthy'
import { BubbleTitle } from '@/components/liv-score-card/elements'
import LivScoreCard from '@/components/liv-score-card'
import ProfileRequired from '@/components/profile-required'
import { fontSizes, colors, fontWeights, breakpoints } from '@/styles'
import useFinancialFiles from '@/hooks/use-financial-files'
import useMediaQuery from '@/hooks/use-media-query'
import { Reveal } from '@/components/animations'
import { ParagraphSmall } from '@/components/text'
import Form from './liv-score-form'
import { PanelHeader, PanelTitle, StyledPanel, RequiredText } from '../elements'

const Spacer = styled.div`
  height: 40px;
`

const Panel = styled(StyledPanel)`
  padding-bottom: 0;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    align-items: center;
  }
`

const DocumentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 0;
  margin: 32px 52px 0;
  border-top: 1px dashed #d9d7d8;

  ${props =>
    props.isSubmittedCard &&
    `
    padding: 25px 0 0; 
    margin: 25px 52px 0;
  `};

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 0 20px;
  }
`

const AddDocsTextSmall = styled.p`
  font-weight: ${fontWeights.medium};
  color: ${colors.darkGrey};
  font-size: ${fontSizes.medium};
  line-height: 24px;
  letter-spacing: -0.08px;
  margin: 0;
`

const AddDocsItem = styled.div`
  display: flex;
  justify-content: space-between;
`

const DocText = styled.span`
  font-size: ${fontSizes.regular};
  line-height: 28px;
`

const DocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0 8px;
  margin-top: 20px;
`

const ViewText = styled.button`
  display: inline-flex;
  background: none;
  border: none;
  padding: 0;
  color: ${colors.link};
  cursor: pointer;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.regular};
  margin-top: 20px;
`

const UnavailableText = styled(ParagraphSmall)`
  max-width: 492px;
`

const LivScore = ({
  data = {},
  userRole,
  livScore,
  editable = false,
  editProps = {},
  showRequiredWarning = false,
  listingData = false,
  hideTitle = false
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isRenter = userRole === 'tenant'
  const secureFiles = useFinancialFiles(data?.files?.user_secure_files, userRole)

  const isSubmitted = data?.liv_verified_state !== 'pending'
  const isFailed = data?.liv_verified_state === 'failed'
  const isNotEnoughInfo = data?.international_user === '1'
  const showDocuments = isSubmitted && secureFiles.length > 0
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const paddingY = !isMobile ? 40 : 24

  const fetchReportStatus = async () => {
    const body = await dispatch.livScore.checkCreditReportStatus()
    dispatch.profile.update(['check_credit_report_status'], body ?? {})
  }

  useEffect(() => {
    if (isRenter) fetchReportStatus()
  }, [dispatch, isRenter])

  return (
    <div>
      {!hideTitle && (
        <>
          <PanelHeader>
            <PanelTitle>
              {t('r.profile.view.liv_score.subtitle', 'Trust Score')}
              <ProfileRequired fields={['liv_verified_state']} area="liv_score" />
            </PanelTitle>
          </PanelHeader>
          {showRequiredWarning && (
            <RequiredText>{t('r.profile.view.required.label', '*Required')}</RequiredText>
          )}
        </>
      )}
      {isFailed && (
        <Box padding="small" borderRadius="large" background="criticalLight" marginTop="xsmall">
          <Notice tone="critical">
            <Text size="small">
              {t(
                'r.trust_score_card.check_failed_info_text',
                'We could not verify your credit check information. Please check that the information provided is correct and resubmit below.'
              )}
            </Text>
          </Notice>
        </Box>
      )}
      <Panel hSpace={0} vSpace={paddingY}>
        <Wrapper>
          <LivScoreCard
            user={data}
            userRole={userRole}
            creditDetails={livScore}
            professionId={data.job_title}
            csoVerifiedState={data.cso_verified_state}
            salarySort={data.salary_sort}
            listingData={listingData}
            showDetails
            secureFiles={showDocuments ? secureFiles : []}
            data-testid="livscore-card"
          >
            <Reveal isOpen id="trust-docs">
              <>
                <Form
                  userRole={userRole}
                  editProps={editProps}
                  editable={!isSubmitted || isFailed || isNotEnoughInfo}
                  isSubmitted={isSubmitted}
                  isFailed={isFailed}
                />
                {showDocuments && (
                  <DocumentsWrapper data-testid="document-list" isSubmittedCard={isSubmitted}>
                    <AddDocsTextSmall>
                      {t(
                        'r.profile.view.additional_documents.text',
                        'Additional Documents Uploaded'
                      )}
                    </AddDocsTextSmall>
                    {secureFiles.map(({ docType, clickId, onClick, ...secureFile }) => (
                      <Fragment key={secureFile.tag}>
                        <AddDocsItem data-testid="document-item">
                          <DocItem>
                            <DocText>{docType}</DocText>
                          </DocItem>
                          {isTruthy(secureFile.deleted) ? (
                            <BubbleTitle
                              label={t(
                                'l.application.view.additional_document_unavailable.text',
                                'Unavailable'
                              )}
                              align="flex-end"
                              size="medium"
                              top={0}
                            >
                              {t(
                                'l.application.view.additional_document_unavailable.text',
                                'Unavailable'
                              )}
                            </BubbleTitle>
                          ) : (
                            <ViewText id={clickId} onClick={onClick} type="button">
                              {t('r.profile.view.view.text', 'View')}
                            </ViewText>
                          )}
                        </AddDocsItem>
                        {isTruthy(secureFile.deleted) && (
                          <UnavailableText>
                            {t(
                              'l.application.view.additional_document_unavailable.description',
                              "This document has either been deleted by the renter, or the liv.rent team has removed it as it didn't meet our verification requirements."
                            )}
                          </UnavailableText>
                        )}
                      </Fragment>
                    ))}
                  </DocumentsWrapper>
                )}
              </>
            </Reveal>
            <Spacer />
          </LivScoreCard>
        </Wrapper>
      </Panel>
    </div>
  )
}

export default withRouter(memo(LivScore))
